// Pure geometry helpers for lesson-page text highlighting.
//
// Highlights are addressed by character offsets into the lesson's clean text
// (no markup). These helpers operate purely on numbers / plain objects so the
// overlap- and table-avoidance rules can be reasoned about and unit-tested
// without touching the DOM. The DOM walking that produces `tableRanges` lives
// in Lesson.jsx; everything here is a pure function of its inputs.

/**
 * True when `offset` falls inside any table character range.
 * Ranges are half-open: [start, end).
 */
export const offsetInTableRange = (offset, tableRanges = []) =>
  tableRanges.some((r) => offset >= r.start && offset < r.end);

/**
 * Given a selection [start, end), return the start offset of the earliest table
 * range it intersects, or null if it touches no table. Used to trim a selection
 * back so highlights never extend into a table.
 */
export const firstTableStartWithin = (start, end, tableRanges = []) => {
  let earliest = null;
  for (const r of tableRanges) {
    if (r.start < end && r.end > start) {
      if (earliest === null || r.start < earliest) earliest = r.start;
    }
  }
  return earliest;
};

/**
 * Trim a candidate selection [start, end) so it does not overlap an existing
 * highlight. Mirrors the original first-overlap behavior:
 *   - overlaps the start of an existing highlight -> keep the leading slice
 *   - overlaps the end of an existing highlight   -> keep the trailing slice
 *   - fully contained in an existing highlight     -> reject (return null)
 *   - no overlap                                    -> return as-is
 *
 * `existingHighlights` is an array of objects with numeric `start`/`end`.
 */
export const findValidSegment = (start, end, existingHighlights = []) => {
  const sorted = [...existingHighlights].sort((a, b) => a.start - b.start);

  for (const highlight of sorted) {
    // Selection starts before the highlight and overlaps it.
    if (start < highlight.start && end > highlight.start) {
      return { start, end: highlight.start };
    }
    // Selection overlaps the tail of the highlight.
    if (start < highlight.end && end > highlight.end) {
      return { start: highlight.end, end };
    }
    // Selection is fully inside an existing highlight.
    if (start >= highlight.start && end <= highlight.end) {
      return null;
    }
  }

  return { start, end };
};
